import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Tooltip,
  Drawer,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  Button,
  useTheme,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChapterSelector from "./ChapterSelector";
import VerseChat from "./VerseChat";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useLanguage } from "../contexts/LanguageContext";
import { formatMessage } from "../locales";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import VerseDrawer from "./VerseDrawer";
import { HISTORICAL_DOCUMENTS } from "./historicalDocs";
import { getHighlight, setHighlight } from "./highlightUtils";
import GavelIcon from "@mui/icons-material/Gavel";
import PublicIcon from "@mui/icons-material/Public";
import PsychologyIcon from "@mui/icons-material/Psychology";
import BalanceIcon from "@mui/icons-material/Balance";
import SearchIcon from "@mui/icons-material/Search";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import EmailSignup from "./EmailSignup";

const HIGHLIGHT_COLORS = [
  "#fff59d", // yellow
  "#90caf9", // blue
  "#a5d6a7", // green
  "#ffab91", // orange
  "#f48fb1", // pink
  "#ce93d8", // purple
];

// Dark mode highlight colors
const DARK_HIGHLIGHT_COLORS = [
  "#e6b800", // amber
  "#4a6fa5", // muted blue
  "#4a8c4a", // muted green
  "#e67700", // amber orange
  "#a35c7a", // muted pink
  "#8c5c99", // muted purple
];

// Add descriptions for historical documents
const HISTORICAL_DOCUMENT_DESCRIPTIONS = {
  "Dead Sea Scrolls":
    "Ancient Jewish manuscripts discovered in the Qumran caves, containing the oldest known copies of biblical texts",
  "Codex Vaticanus":
    "One of the oldest complete copies of the Bible, written in Greek and housed in the Vatican Library",
  "Codex Sinaiticus":
    "One of the oldest complete copies of the Bible, discovered at St. Catherine's Monastery in Sinai",
};

function getHighlightKey(book, chapter, verse) {
  return `highlight-${book}-${chapter}-${verse}`;
}

function ChapterView() {
  const { bookId, chapterNumber, verseNumber } = useParams();
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [currentBook, setCurrentBook] = useState(null);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [chapterData, setChapterData] = useState(null);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState(null);
  const [highlightColor, setHighlightColor] = useState(null);
  const [textSize, setTextSize] = useState(18);
  const [activeTab, setActiveTab] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const theme = useTheme();
  const { messages } = useLanguage();
  const highlightColors =
    theme.palette.mode === "dark" ? DARK_HIGHLIGHT_COLORS : HIGHLIGHT_COLORS;
  const [fontType, setFontType] = useState("default");
  const [topicSearch, setTopicSearch] = useState("");
  const [topicOptions, setTopicOptions] = useState([]);
  const [topicLoading, setTopicLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [highlightRefresh, setHighlightRefresh] = useState(0);

  // Font family mapping
  const fontFamilyMap = {
    default: undefined,
    opendyslexic: "OpenDyslexic, Arial, sans-serif",
    serif: "Georgia, Times New Roman, serif",
    sans: "Arial, Helvetica, sans-serif",
    mono: "Courier New, monospace",
    merriweather: "Merriweather, Georgia, serif",
    lato: "Lato, Arial, sans-serif",
  };

  // Fetch books on mount
  useEffect(() => {
    axios.get(`${API_URL}/api/books`).then((res) => setBooks(res.data));
  }, []);

  // Set current book/chapter from params or default
  useEffect(() => {
    if (!books.length) return;
    let book = null;
    if (/^\d+$/.test(bookId)) {
      book = books.find((b) => b.number === parseInt(bookId));
    } else {
      const normalized = bookId.replace(/[-_]/g, " ").toLowerCase();
      book = books.find(
        (b) => b.name.toLowerCase().replace(/[-_]/g, " ") === normalized
      );
    }
    if (!book) {
      setError("Book not found");
      return;
    }
    setCurrentBook(book);
    const chapterNum = parseInt(chapterNumber) || 1;
    setCurrentChapter(chapterNum);
  }, [books, bookId, chapterNumber]);

  // Fetch chapter data
  useEffect(() => {
    if (!currentBook || !currentChapter) return;
    axios
      .get(
        `${API_URL}/api/books/${currentBook.number}/chapters/${currentChapter}`
      )
      .then((res) => setChapterData(res.data))
      .catch(() => setError("Failed to load chapter."));
  }, [currentBook, currentChapter]);

  // Handle verse param (for direct links)
  useEffect(() => {
    if (!chapterData || !verseNumber) return;
    const verse = chapterData.verses.find(
      (v) => v.number === parseInt(verseNumber)
    );
    if (verse) setSelectedVerse({ ...verse, chapterNumber: currentChapter });
  }, [chapterData, verseNumber, currentChapter]);

  // Always show Verse tab first when a verse is selected
  useEffect(() => {
    if (selectedVerse) setActiveTab(0);
  }, [selectedVerse]);

  useEffect(() => {
    if (selectedVerse && currentBook) {
      const color = getHighlight(
        currentBook.name,
        selectedVerse?.chapterNumber,
        selectedVerse?.number
      );
      setHighlightColor(color);
    }
  }, [selectedVerse, currentBook]);

  // Fetch topics as user types
  useEffect(() => {
    if (!topicSearch) {
      setTopicOptions([]);
      return;
    }
    setTopicLoading(true);
    axios
      .get(`${API_URL}/api/topics?search=${encodeURIComponent(topicSearch)}`)
      .then((res) => setTopicOptions(res.data))
      .catch(() => setTopicOptions([]))
      .finally(() => setTopicLoading(false));
  }, [topicSearch]);

  const handleBookChange = (bookIdOrNumber) => {
    let bookParam = bookIdOrNumber;
    let bookObj = null;
    if (typeof bookIdOrNumber === "number") {
      bookObj = books.find((b) => b.number === bookIdOrNumber);
      if (bookObj) bookParam = bookObj.name.replace(/ /g, "-");
    } else {
      bookObj = books.find((b) => b.name.replace(/ /g, "-") === bookIdOrNumber);
    }
    setCurrentBook(bookObj);
    setCurrentChapter(1);
    navigate(`/read/book/${bookParam}/chapter/1`);
  };

  const handleChapterChange = (chapterNum) => {
    setCurrentChapter(chapterNum);
    const bookIdStr = currentBook ? currentBook.name.replace(/ /g, "-") : "";
    navigate(`/read/book/${bookIdStr}/chapter/${chapterNum}`);
  };

  const handleVerseClick = (verse) => {
    setSelectedVerse({ ...verse, chapterNumber: currentChapter });
    const bookIdStr = currentBook ? currentBook.name.replace(/ /g, "-") : "";
    navigate(
      `/read/book/${bookIdStr}/chapter/${currentChapter}/verse/${verse.number}`
    );
  };

  const handleCloseMenu = () => {
    setSelectedVerse(null);
    setChatMessages([]);
    setChatInput("");
    setIsChatLoading(false);
    const bookIdStr = currentBook ? currentBook.name.replace(/ /g, "-") : "";
    navigate(`/read/book/${bookIdStr}/chapter/${currentChapter}`);
  };

  const handleShare = async () => {
    if (!selectedVerse || !currentBook) return;
    const reference = `${currentBook.name} ${currentChapter}:${selectedVerse.number}`;
    const shareUrl = `${window.location.origin}/read/book/${bookId}/chapter/${currentChapter}/verse/${selectedVerse.number}`;
    const shareText = `${reference}\n"${selectedVerse.text}"\n\nRead more at OpenCreed:`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: reference,
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
          setSnackbarMessage("Link copied to clipboard!");
          setSnackbarOpen(true);
        }
      }
    } else {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setSnackbarMessage("Link copied to clipboard!");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const goToPrevChapter = () => {
    if (currentChapter > 1) {
      handleChapterChange(currentChapter - 1);
    }
  };
  const goToNextChapter = () => {
    if (currentBook && currentChapter < currentBook.chapters.length) {
      handleChapterChange(currentChapter + 1);
    }
  };

  const handleHighlight = (color) => {
    setHighlightColor(color);
    setHighlight(
      currentBook.name,
      selectedVerse?.chapterNumber,
      selectedVerse?.number,
      color
    );
  };

  // Helper to parse verseRef like "Exodus 20.1-Exodus 20.26"
  function goToVerseRef(verseRef) {
    // Only support single verse or range within same chapter for now
    // e.g. "Exodus 20.1" or "Exodus 20.1-Exodus 20.26"
    const match = verseRef.match(
      /([A-Za-z0-9 ]+) (\d+)\.(\d+)(?:-([A-Za-z0-9 ]+) (\d+)\.(\d+))?/
    );
    if (!match) return;
    const bookName = match[1];
    const chapterNum = parseInt(match[2]);
    const verseNum = parseInt(match[3]);
    // Find book by name
    const book = books.find(
      (b) => b.name.toLowerCase() === bookName.toLowerCase()
    );
    if (!book) return;
    const bookIdStr = book.name.replace(/ /g, "-");
    navigate(`/read/book/${bookIdStr}/chapter/${chapterNum}/verse/${verseNum}`);
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  if (!books.length || !chapterData) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }
  const currentBookData = currentBook;

  return (
    <Container
      maxWidth="md"
      sx={{ px: { xs: 0, sm: 3 }, mt: { xs: 2, sm: 4 }, position: "relative" }}
    >
      {/* Topical Search Bar */}
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          freeSolo
          options={topicOptions}
          getOptionLabel={(option) => option.topic || ""}
          loading={topicLoading}
          inputValue={topicSearch}
          onInputChange={(_, value) => {
            setTopicSearch(value);
            setSelectedTopic(null);
          }}
          onChange={(_, value) => {
            if (value && value.topic) {
              navigate(
                `/read/topical?search=${encodeURIComponent(value.topic)}`
              );
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Topics"
              variant="outlined"
              size="small"
              fullWidth
            />
          )}
        />
      </Box>
      <ChapterSelector
        books={books}
        currentBook={currentBook}
        currentChapter={currentChapter}
        onBookChange={handleBookChange}
        onChapterChange={handleChapterChange}
        textSize={textSize}
        onTextSizeChange={setTextSize}
        fontType={fontType}
        onFontTypeChange={setFontType}
      />
      <Box
        sx={{
          position: "fixed",
          left: { xs: 8, md: 32 },
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1200,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={goToPrevChapter}
          disabled={currentChapter === 1}
          sx={{
            minWidth: 0,
            borderRadius: "50%",
            p: 1.5,
            opacity: 0.7,
            transition: "opacity 0.2s, transform 0.1s",
            "&:active": { transform: "scale(0.92)" },
          }}
        >
          <KeyboardArrowLeftIcon fontSize="large" />
        </Button>
      </Box>
      <Box
        sx={{
          position: "fixed",
          right: { xs: 8, md: 32 },
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1200,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={goToNextChapter}
          disabled={currentChapter === (currentBookData?.chapters.length || 0)}
          sx={{
            minWidth: 0,
            borderRadius: "50%",
            p: 1.5,
            opacity: 0.7,
            transition: "opacity 0.2s, transform 0.1s",
            "&:active": { transform: "scale(0.92)" },
          }}
        >
          <KeyboardArrowRightIcon fontSize="large" />
        </Button>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <Typography variant="h5" color="primary" sx={{ fontWeight: "bold" }}>
          {currentBookData?.name} {currentChapter}
        </Typography>
      </Box>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="body1"
          component="div"
          sx={{
            lineHeight: 1.8,
            fontSize: textSize,
            fontFamily: fontFamilyMap[fontType],
          }}
        >
          {chapterData.verses?.map((verse) => {
            const colorIndex = getHighlight(
              currentBookData?.name,
              currentChapter,
              verse.number
            );
            const bgColor =
              typeof colorIndex === "number" &&
              colorIndex >= 0 &&
              colorIndex < highlightColors.length
                ? highlightColors[colorIndex]
                : "inherit";
            return (
              <React.Fragment key={verse.number + "-" + highlightRefresh}>
                <Typography
                  component="span"
                  onClick={() => handleVerseClick(verse)}
                  sx={{
                    cursor: "pointer",
                    color:
                      selectedVerse?.number === verse.number &&
                      selectedVerse?.chapterNumber === currentChapter
                        ? "primary.main"
                        : "inherit",
                    fontWeight:
                      selectedVerse?.number === verse.number
                        ? "bold"
                        : "normal",
                    backgroundColor: bgColor,
                    borderRadius: 1,
                    px: bgColor !== "inherit" ? 0.5 : 0,
                    transition: "background 0.2s",
                    textDecoration: "none",
                    "&:hover": {
                      color: "primary.main",
                      textDecoration: "underline",
                    },
                    display: "inline",
                    fontSize: textSize,
                    fontFamily: fontFamilyMap[fontType],
                  }}
                >
                  <sup
                    style={{
                      fontSize: textSize * 0.6,
                      verticalAlign: "super",
                      marginRight: 2,
                      fontWeight: "bold",
                      color: "#1976d2",
                    }}
                  >
                    {verse.number}
                  </sup>
                  {verse.text}
                </Typography>{" "}
              </React.Fragment>
            );
          })}
        </Typography>
      </Paper>
      <EmailSignup />
      <VerseDrawer
        open={!!selectedVerse}
        onClose={handleCloseMenu}
        verse={selectedVerse}
        bookName={currentBookData?.name}
        chapterNumber={currentChapter}
        highlightColor={highlightColor}
        setHighlightColor={setHighlightColor}
        getHighlight={getHighlight}
        setHighlight={(book, chapter, verse, colorIndex) => {
          setHighlight(book, chapter, verse, colorIndex);
          setHighlightRefresh((r) => r + 1);
        }}
        onHighlightChange={() => setHighlightRefresh((r) => r + 1)}
        fontType={fontType}
        theme={theme}
        historicalDocs={HISTORICAL_DOCUMENTS}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
}

export default ChapterView;
